<template>

    <div>
        <v-container fluid>

            <div class="d-flex align-center py-3">
                <div>
                    <div class="display-1">Bookings</div>
                </div>
                <v-spacer></v-spacer>
                <v-btn
                        v-if="hasPermission('booking-create')"
                        color="info"
                        dark
                        class="mb-2"
                        :to="{name: 'booking-create'}"
                >
                    <i class="fas fa-plus mr-2"></i> New Booking
                </v-btn>
            </div>

            <v-row>
                <v-col cols="6">
                    <v-toolbar>
                        <v-menu
                                ref="menu"
                                v-model="pickerOpen"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                max-width="290px"
                                min-width="290px"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                        v-model="formattedDate"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        max-width="290px"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                    :multiple="pickerRange"
                                    :range="pickerRange"
                                    :show-week="true"
                                    :type="pickerType"
                                    @change="[updatePeriod, pickerOpen = false]"
                                    first-day-of-week="1"
                                    v-model="date"
                            >
                            </v-date-picker>
                        </v-menu>

                        <v-btn-toggle
                                v-model="selectedType"
                                tile
                                group
                                mandatory
                                @change="selectType(selectedType)"
                        >
                            <v-btn value="date">
                                Day
                            </v-btn>

                            <v-btn value="week">
                                Week
                            </v-btn>

                            <v-btn value="month">
                                Month
                            </v-btn>

                        </v-btn-toggle>
                        <v-btn class="mr-2" @click="previous">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        <v-btn @click="next">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </v-toolbar>

                </v-col>


            </v-row>
            <div class="d-flex flex-row">
                <h2 class="mr-2">{{title()}}</h2>
            </div>
            <v-progress-linear
                    :active="$apollo.loading"
                    :indeterminate="$apollo.loading"
                    color="indigo accent-4"
            ></v-progress-linear>
            <v-simple-table>
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th class="text-left">ID</th>
                        <th class="text-left">Date</th>
                        <th class="text-left">Company</th>
                        <th class="text-left">Title</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Booker</th>
                        <th class="text-left">Rep</th>
                        <th class="text-left">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="booking in bookings" :key="booking.id">
                        <td>
                            <v-btn :to="{name:'booking-view', params: {id: booking.id}}"
                                   small
                            >BK-{{ booking.id }}
                            </v-btn>
                        </td>
                        <td>{{ booking.bookingDate | formatDate('ddd, D MMM') }}</td>
                        <td>{{ booking.contactCompany }}</td>
                        <td>{{ booking.courseTitle }}</td>
                        <td><BookingStatusChip v-model="booking.status" small/></td>
                        <td>{{ booking.contactName }}</td>
                        <td>{{ booking.accountManager.firstName }}</td>
                        <td>{{ booking.totalPrice|toCurrency }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                      <tr v-for="manager in accountManagerTotals" :key="manager.name">
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{{ manager.name }}</td>
                        <td>{{manager.total|toCurrency}}</td>
                      </tr>
                        <tr>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td><strong>Total:</strong></td>
                            <td>{{totalValue|toCurrency}}</td>
                        </tr>
                    </tfoot>
                </template>
            </v-simple-table>
        </v-container>
    </div>
</template>

<script>

    import Dashboard from "../layouts/Dashboard"
    import {mapGetters} from 'vuex'
    import BOOKINGS_LIST from "../graphql/BookingsRange.gql"
    import moment from 'moment'
    import BookingStatusChip from "../components/Booking/BookingStatusChip";

    const DAY_DATE_FORMAT = 'YYYY-MM-DD'
    const DAY_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
    const DAY_MONTH_FORMAT = 'YYYY-MM';
    const TODAY = moment().set({hour: 0, minute: 0, second: 0, millisecond: 0});

    export default {
        name: "speedy",
        components: {BookingStatusChip},
        metaInfo: {
            title: 'Bookings'
        },
        data() {
            return {
                selectedType: 'month',
                pickerType: 'month',
                pickerRange: false,
                pickerOpen: false,
                date: TODAY.format(DAY_DATE_FORMAT),
                start: TODAY.startOf('month').format(DAY_DATETIME_FORMAT),
                end: TODAY.endOf('month').format(DAY_DATETIME_FORMAT),
            }
        },
        apollo: {
            bookings: {
                query: BOOKINGS_LIST,
                variables() {
                    return {start: this.start, end: this.end}
                },
                update: data => data.bookings
            },
        },

        watch: {

            date() {
                this.updatePeriod();
            }
        },
        computed: {
            ...mapGetters('auth', [
                'isAuthenticated',
                'hasPermission',
                'getUser',
            ]),
            totalValue() {
              return this.$_.reduce(this.bookings, (memo, booking) => {
                  return memo + booking.totalPrice
              }, 0)
            },
            formattedDate() {
                if (this.selectedType === 'week') {
                    return 'Week ' + moment(this.date[0]).isoWeek();
                }

                if (this.selectedType === 'date') {
                    return moment(this.date).format("ddd, D MMMM YYYY");
                }
                if (this.selectedType === 'month') {
                    return moment(this.date).format("MMMM, YYYY");
                }
                return '';
            },
            accountManagerTotals() {

              return this.$_.reduce(this.bookings, (result, booking) => {

                let manager  = this.$_.findIndex(result, {name: booking.accountManager.firstName})

                if(manager === -1){
                  manager = {name:booking.accountManager.firstName, total: 0}
                  result.push(manager)
                } else {
                  manager = result[manager]
                }

                manager.total += booking.totalPrice

                return result;
              }, [])

            }

        },
        created() {
            this.$emit('update:layout', Dashboard);
        },
        methods: {

            selectType(newType) {

                this.date = TODAY.format(DAY_DATE_FORMAT)
                if (newType === 'week') {
                    console.log('switch to week');
                    this.pickerType = 'date';
                    this.pickerRange = true;
                    this.date = [moment().format(DAY_DATE_FORMAT)]
                }

                if (newType === 'date') {
                    this.pickerType = 'date';
                    this.pickerRange = false;

                }

                if (newType === 'month') {
                    this.pickerType = 'month';
                    this.pickerRange = false;
                }

                this.updatePeriod();
            },

            title() {

                if (this.selectedType === 'week') {
                    return 'Week ' + moment(this.date[0]).isoWeek();
                }

                if (this.selectedType === 'date') {
                    return moment(this.date).format("ddd, D MMMM YYYY");
                }
                if (this.selectedType === 'month') {
                    return moment(this.date).format("MMMM, YYYY");
                }

            },

            next() {
                if (this.selectedType === 'week') {
                    this.date = moment(this.date[0]).add(1, 'week').format(DAY_DATE_FORMAT);
                }
                if (this.selectedType === 'date') {
                    this.date = moment(this.date).add(1, 'days').format(DAY_DATE_FORMAT);
                }
                if (this.selectedType === 'month') {
                    this.date = moment(this.date).add(1, 'month').format(DAY_MONTH_FORMAT)
                }
                this.updatePeriod();
            },

            previous() {
                if (this.selectedType === 'week') {
                    this.date = moment(this.date[0]).subtract(1, 'week').format(DAY_DATE_FORMAT);
                }
                if (this.selectedType === 'date') {
                    this.date = moment(this.date).subtract(1, 'days').format(DAY_DATE_FORMAT);
                }
                if (this.selectedType === 'month') {
                    this.date = moment(this.date).subtract(1, 'month').format(DAY_MONTH_FORMAT)
                }
                this.updatePeriod();
            },

            updatePeriod() {
                if (this.selectedType === 'week') {
                    let firstSelectedDate;
                    if (Array.isArray(this.date)) {
                        firstSelectedDate = moment(this.date[0])
                    } else {
                        firstSelectedDate = moment(this.date)
                    }

                    this.start = firstSelectedDate.startOf('week').format(DAY_DATE_FORMAT);
                    this.end = firstSelectedDate.endOf('week').format(DAY_DATE_FORMAT);


                    if (this.date[0] !== this.start || this.date[1] !== this.end) {
                        this.date = [this.start, this.end]
                    }
                    return;
                }

                if (Array.isArray(this.date)) {
                    this.date = this.date[0];
                }

                if (this.selectedType === 'date') {
                    this.start = moment(this.date).format(DAY_DATETIME_FORMAT);
                    this.end = this.start;
                }


                if (this.selectedType === 'month') {
                    let month = moment(this.date)
                    this.start = month.startOf('month').format(DAY_DATETIME_FORMAT);
                    this.end = month.endOf('month').format(DAY_DATETIME_FORMAT);
                }
            }
        }
    };
</script>
